import type { PurchasePointsError } from "@/features/purchasePoints/errors/purchasePointsError";
import { isAccessCode } from "@/features/purchasePoints/errors/purchasePointsError";
import type { PurchasePointsBalance } from "@/features/purchasePoints/types/purchasePointsBalance.type";
import type { PurchasePointsReward } from "@/features/purchasePoints/types/purchasePointsReward.type";

export type SummaryState = "hidden" | "loading" | "failed" | "dormant" | "ready";

type BalanceReading = {
  balance: PurchasePointsBalance | null
  isLoading: boolean
  isDisabled: boolean
  error: PurchasePointsError | null
};

type RewardsReading = {
  rewards: PurchasePointsReward[]
  isLoading: boolean
  error: PurchasePointsError | null
};

export const hasNeverEarned = (balance: PurchasePointsBalance, rewards: PurchasePointsReward[]): boolean =>
  balance.available_points === 0
  && balance.pending_points === 0
  && balance.expired_points === 0
  && balance.converted_points === 0
  && rewards.length === 0;

/**
 * Pure by design: the widget only ever has to feed it two readings and render
 * the result, instead of re-deriving this branching in the component itself.
 */
export const readSummaryState = (balance: BalanceReading, rewards: RewardsReading): SummaryState => {
  if (balance.isDisabled) return "hidden";

  // A refusal on either reading is an absence, not a failure: the widget
  // disappears rather than explain why it cannot show what was refused.
  if ((balance.error && isAccessCode(balance.error.code)) || (rewards.error && isAccessCode(rewards.error.code))) {
    return "hidden";
  }

  if (balance.error) return "failed";

  // Both readings are awaited before a verdict: deciding on the balance alone
  // would flash dormant for an instant whenever the gains are still in flight.
  if (balance.isLoading) return "loading";
  if (rewards.isLoading) return "loading";

  if (!balance.balance) return "hidden";

  // A rewards failure proves nothing about whether gains exist: it must never
  // be read as dormant, the state a technical outage cannot decide.
  if (!rewards.error && hasNeverEarned(balance.balance, rewards.rewards)) return "dormant";

  return "ready";
};
